import { fetcher, useAPI, useAPIMutation } from '@/api/api';
import { IFEnterGameRsp } from '@/api/apiRsp/IFEnterGameRsp';
import { IFEnterGameReq } from '@/api/apiReq/IFEnterGameReq';
import { IFGameTypeReq } from '@/api/apiReq/IFGameTypeReq';
import { IFSearchRsp } from '@/api/apiRsp/IFSearchRsp';
import { IFSearchReq } from '@/api/apiReq/IFSearchReq';
import { IFGameItem, IFGameTypeRsp } from '@/api/apiRsp/IFGameTypeRsp';
import { IFGameDetailReq } from '@/api/apiReq/IFGameDetail';
import { useBeforeSetting } from './beforeSetting';
import { IFBetInfo } from '@/api/apiRsp/IFGameOriginal';
import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { getUrlParam, parseQueryString } from '@/Core/utils/system';

export interface IGame {
  gid: string;
  id: number;
  platform: string;
  name: string;
  cn: string;
  en: string;
  pu: string;
  icon: string;
  url: string;
  status: number;
  recommend: number;
  type: string[];
  ishot: number;
}

export function useWebConfig() {
  const { data, error, isLoading, revalidate } = useBeforeSetting();
  return {
    appConfig: data,
    isLoading,
    error,
    revalidate,
  };
}

export interface GameBody {
  favoriteList: Array<unknown>; //收藏游戏
  games: Array<unknown>; //各个分类游戏
  hotList: Array<unknown>; //热门游戏
  originalList: Array<unknown>; //原创游戏
  platforms: Array<unknown>; //游戏平台
  playedList: Array<unknown>; //最近游戏
  recommendList: Array<unknown>; //推荐游戏
}

//进入游戏接口
export function useEnterGame() {
  const { data, trigger, error, isMutating } = useAPIMutation<IFEnterGameRsp, IFEnterGameReq>({ path: '/api/game/enter', type: 'post' });
  return {
    data,
    trigger,
    error,
    isMutating,
  };
}
//退出游戏接口
export function useExitGame() {
  const { data, trigger } = useAPIMutation<unknown, unknown>({ path: '/api/game/exit', type: 'get' });
  return {
    data,
    exitTrigger: trigger,
  };
}

//获取游戏详情接口
export function useGameDetailPlus(gameId) {
  const fetch = ()=>{
    //
    return fetcher<IFGameItem>({path: '/api/game/get',type:'get'},{arg: {
      gameId:gameId
    }})
  }
  const { data, isValidating, error, mutate , isLoading} = useSWR<IFGameItem>(gameId ? `gameDetail?gameId=${gameId}` : null,fetch,{dedupingInterval: 5 * 60 * 1000})
  return { data, isValidating, error, mutate , isLoading}
}

//游戏详情接口
export function useGameDetail() {
  const { trigger, data, isMutating, error } = useAPIMutation<IFGameItem, IFGameDetailReq>({
    path: '/api/game/get',
    type: 'get',
  });

  return {
    triggerDetail: trigger,
    detailData: data,
    isLoading: isMutating,
    error,
  };
}
//游戏收藏
export function useFavorite() {
  const { data, trigger, isMutating } = useAPIMutation<unknown, unknown>({ path: 'api/game/collect', type: 'post' });
  return {
    data,
    triggerFavorite: trigger,
    isMutating,
  };
}

export function useRecommendListPlus(pageSize,page) {
  const fetcherRecommend = (arg) => {
    const params = parseQueryString(arg.split('?')[1]);
    return fetcher<IFGameTypeRsp>({ path: '/api/game/recommend/list', type: 'get' },{arg: params})
  }
  return useSWR<IFGameTypeRsp>(`getRecommend?pageSize=${pageSize}&page=${page}`,fetcherRecommend)
}

//收藏游戏列表
export function useFavoriteList() {
  const { data, trigger , isMutating } = useAPIMutation<IFGameTypeRsp, IFGameTypeReq>({ path: '/api/game/favorite/list', type: 'get' });
  return {
    data,
    trigger,
    isMutating
  };
}

//最近游戏列表
export function usePlayedList() {
  const { data, trigger , isMutating} = useAPIMutation<IFGameTypeRsp, IFGameTypeReq>({ path: '/api/game/played/list', type: 'get' });
  return {
    data,
    trigger,
    isMutating
  };
}

//原创游戏列表
export function useOriginalList() {
  const { data, trigger } = useAPIMutation<IFGameTypeRsp, IFGameTypeReq>({ path: '/api/game/original/list', type: 'get' });
  return {
    data,
    trigger,
  };
}

//热门游戏列表
export function useHotListPlus(pageSize,page) {
  const fetcherHotList = (arg) => {
    const params = parseQueryString(arg.split('?')[1]);
    return fetcher<IFGameTypeRsp>({ path: '/api/game/hot/list', type: 'get' },{arg: params})
  }
  return useSWR<IFGameTypeRsp>(`getHotList?pageSize=${pageSize}&page=${page}`,fetcherHotList,{
    dedupingInterval: 60 * 1000
  })
}

//搜索游戏列表
export function useSearchGame() {
  const { data, trigger } = useAPIMutation<Array<IFSearchRsp>, IFSearchReq>({ path: '/api/game/search/list', type: 'get' });
  return {
    data,
    trigger,
  };
}

//游戏类型列表 platformCode 和 gameType 不能同时为空）
export function useGameTypeListPlus({
  page,
  pageSize,
  gameType,
  isAzSort,
  platformCodes
}: any) {
  const fetcherTypeList = (arg) => {
    const params = parseQueryString(arg.split('?')[1]);
    return fetcher<IFGameTypeRsp>({ path: '/api/game/game_type/list', type: 'post' },{arg: {
      ...params,
      platformCodes:platformCodes
    }})
  }
  let key = 'getTypeList?';
  key += `pageSize=${pageSize}`;
  key += `&page=${page}`;
  key += `&gameType=${gameType}`;
  key += `&isAzSort=${isAzSort}`;
  key += `&platformCodes=${JSON.stringify(platformCodes)}`
  return useSWR<IFGameTypeRsp>(key,fetcherTypeList,{
    dedupingInterval: 120 * 1000
  })
}

export function useGameTypeList() {
  const { data, trigger, isMutating, error } = useAPIMutation<IFGameTypeRsp, IFGameTypeReq>({ path: '/api/game/game_type/list', type: 'post' });
  return {
    data,
    trigger,
    isMutating,
    error,
  };
}


export function useGameList() {
  const { data, trigger, isMutating, error } = useAPIMutation<IFGameTypeRsp, IFGameTypeReq>({ path: '/api/game/gameList', type: 'post' });
  return {
    data,
    trigger,
    isMutating,
    error,
  };
}

//游戏注单列表
export function useAllBetListPlus(params) {
  const fetch = ()=>{
    return fetcher<IFBetInfo[]>({path: '/api/game/allBetOrder', type: 'post'} , {
      arg: params
    })
  }
  return useSWR<IFBetInfo[]>(!params || !params?.gameId ? null : `allBetOrder?params=${JSON.stringify(params)}`,fetch,{
    dedupingInterval: 10 * 1000
  })
}

//游戏注单列表
export function useAllBetList() {
  const { data, trigger, isMutating, error } = useAPIMutation<IFBetInfo[], unknown>({
    path: '/api/game/allBetOrder',
    type: 'post',
  });

  const [betInfo, setBetInfo] = useState<IFBetInfo[]>();
  useEffect(() => {
    if (data) {
      const arr = [...data];
      for (const iterator of arr) {
        iterator['key'] = iterator.betId;
      }
      setBetInfo(arr);
    }
  }, [data]);

  return {
    data: betInfo,
    trigger,
    isMutating,
    error,
  };
}

// 游戏注单列表详情

export function useAllBetListDetail() {
  const { data, trigger, isMutating, error } = useAPIMutation<IFBetInfo[], unknown>({
    path: '/api/game/getOrderByBetId',
    type: 'get',
  });

  return {
    data,
    trigger,
    isMutating,
    error,
  };
}

//获取游戏币种
export function useGetCurrencyTypePlus(currencyType) {
  const fetch = ()=>{
    return fetcher<any>({path: '/api/game/getWalletGameCurrency',type: 'post'},{
      arg: {
        currencyType: currencyType
      }
    })
  }
  return useSWR<any>(!currencyType ? null : `getWalletGameCurrency?currencyType=${currencyType}`,fetch,{
    dedupingInterval: 60 * 1000
  })
}

// 游戏币种获取
export function useGetCurrencyType() {
  const { data, error, trigger, isMutating } = useAPIMutation<any, any>({
    path: '/api/game/getWalletGameCurrency',
    type: 'post',
  });

  return { data, error, trigger, isLoading: isMutating };
}
